import axios from 'axios'
import store from '@/store/store'

/**
 * Build headers for multipart form data requests
 */
function buildMultipartHeaders() {
    var headers = {
        'apikey': process.env.VUE_APP_RTEK8DSR,
        'paraphrase': process.env.VUE_APP_BJ7DC3S
    }
    if (store.state && store.state.token) {
        headers['Authorization'] = 'Bearer ' + store.state.token
    }
    return headers
}

/**
 * Normalize carburant value from OCR to form value
 */
function mapCarburantValue(ocrValue) {
    if (!ocrValue) return null
    var normalized = ocrValue.toLowerCase().trim()

    if (normalized.includes('diesel') || normalized.includes('diésel') || normalized.includes('gazole')) {
        return 'Diésel'
    }
    if (normalized.includes('essence') || normalized.includes('super') || normalized.includes('sp')) {
        return 'Essence'
    }
    if (normalized.includes('hybride')) {
        return 'Hybride'
    }
    if (normalized.includes('electrique') || normalized.includes('électrique') || normalized.includes('elec')) {
        return 'Electrique'
    }
    return null
}

/**
 * Convert date from DD/MM/YYYY to YYYY-MM-DD format
 */
function formatDateForPicker(ocrDate) {
    if (!ocrDate) return ''
    var trimmed = ocrDate.trim()

    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed
    }

    // DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        var parts = trimmed.split('/')
        return parts[2] + '-' + parts[1] + '-' + parts[0]
    }

    // DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
        var parts2 = trimmed.split('-')
        return parts2[2] + '-' + parts2[1] + '-' + parts2[0]
    }

    return trimmed
}

/**
 * Normalize categorie value from OCR to form value
 */
function mapCategorieValue(ocrValue) {
    if (!ocrValue) return null
    var normalized = ocrValue.toLowerCase().trim()

    if (normalized.includes('particulier') || normalized.includes('personnel') || normalized.includes('vp') || normalized.includes('tourisme')) {
        return 'tourisme'
    }
    if (normalized.includes('utilitaire') || normalized.includes('camion') || normalized.includes('fourgon') || normalized.includes('vu')) {
        return 'utilitaire'
    }
    if (normalized.includes('moto') || normalized.includes('cyclo') || normalized.includes('2 roues') || normalized.includes('deux roues')) {
        return '2_roues'
    }
    return null
}

/**
 * Normalize OCR response to standard format
 */
function normalizeOcrResponse(response) {
    var payload = response && response.data
    var data = (payload && (payload.data || payload.result || payload.ocr)) || payload
    if (!data) return null

    var rawCategorie = data.categorie || data.category || data.type_vehicule || ''
    var rawGenre = data.genre || data.genre_vehicule || data.vehicle_genre || data.genreVehicule || ''

    return {
        immatriculation: data.immatriculation || data.registration || data.numero_immatriculation || '',
        marque: data.marque || data.brand || data.make || '',
        modele: data.appelation_com || data.modele || data.model || data.type || '',
        dateCirculation: formatDateForPicker(data.date_mise_circulation || data.date_circulation || data.dateCirculation || data.dateCircule || data.first_registration || ''),
        carburant: mapCarburantValue(data.energie || data.carburant || data.fuel || data.energy),
        categorie: mapCategorieValue(rawCategorie),
        categorieLabel: rawCategorie,
        genre: rawGenre ? String(rawGenre).trim() : '',
        genreLabel: rawGenre ? String(rawGenre).trim() : '',
        puissanceFiscale: data.puissance_fiscale || data.puissance || data.force || data.fiscal_power || data.cv || '',
        cylindree: data.cylindree || data.cylindre || data.cylindrer || data.engine_capacity || '',
        nombrePlaces: data.nombre_places || data.nbplace || data.places || data.seats || '',
        numeroChassis: data.numero_chassis || data.vin || data.chassis || '',
        proprietaire: data.proprietaire || ((data.prenom || '') + ' ' + (data.nom || '')).trim() || data.owner || data.titulaire || '',
        nom: data.nom || '',
        prenom: data.prenom || ''
    }
}

/**
 * Redimensionne et recompresse une image côté navigateur avant envoi.
 * Réduit fortement le poids des photos prises directement avec l'appareil
 * photo mobile (plusieurs Mo à la source), qui allonge l'upload et le
 * traitement OCR sur connexion mobile lente. Les PDF sont laissés tels quels.
 */
function compressImage(file, maxDimension, quality) {
    if (!file || file.type === 'application/pdf' || !file.type.startsWith('image/')) {
        return Promise.resolve(file)
    }

    return new Promise(function (resolve) {
        var objectUrl = URL.createObjectURL(file)
        var img = new Image()

        img.onload = function () {
            URL.revokeObjectURL(objectUrl)

            var width = img.width
            var height = img.height
            var scale = Math.min(1, maxDimension / Math.max(width, height))

            if (scale >= 1) {
                // Déjà assez petite, pas besoin de recompresser
                resolve(file)
                return
            }

            var canvas = document.createElement('canvas')
            canvas.width = Math.round(width * scale)
            canvas.height = Math.round(height * scale)
            var ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

            canvas.toBlob(function (blob) {
                if (!blob) {
                    resolve(file)
                    return
                }
                var compressedFile = new File([blob], file.name, { type: 'image/jpeg' })
                resolve(compressedFile)
            }, 'image/jpeg', quality)
        }

        img.onerror = function () {
            URL.revokeObjectURL(objectUrl)
            resolve(file)
        }

        img.src = objectUrl
    })
}

/**
 * Merge OCR data from recto and verso
 */
function mergeOcrData(rectoData, versoData) {
    var rectoPayload = (rectoData && rectoData.data) || rectoData || {}
    var versoPayload = (versoData && versoData.data) || versoData || {}

    // Merge: prefer non-empty values, recto takes priority for conflicts
    var merged = {}
    var allKeys = Object.keys(Object.assign({}, rectoPayload, versoPayload))

    for (var i = 0; i < allKeys.length; i++) {
        var key = allKeys[i]
        var rectoVal = rectoPayload[key]
        var versoVal = versoPayload[key]

        if (rectoVal && rectoVal !== '') {
            merged[key] = rectoVal
        } else if (versoVal && versoVal !== '') {
            merged[key] = versoVal
        } else {
            merged[key] = ''
        }
    }

    return { data: merged }
}

export default {
    /**
     * Scan carte grise images (recto and verso) via OCR
     * @param {File} rectoFile - Recto image file (required)
     * @param {File} versoFile - Verso image file (required)
     * @returns {Promise<Object>} Extracted carte grise data
     */
    async scanCarteGrise(rectoFile, versoFile) {
        // Recompresse les photos issues de l'appareil photo mobile avant envoi
        // (upload + traitement OCR bien plus lents sur connexion mobile sinon).
        var compressedRecto = await compressImage(rectoFile, 1600, 0.8)
        var compressedVerso = await compressImage(versoFile, 1600, 0.8)

        var formDataRecto = new FormData()
        formDataRecto.append('file', compressedRecto)
        formDataRecto.append('fichier', compressedRecto)

        var formDataVerso = new FormData()
        formDataVerso.append('file', compressedVerso)
        formDataVerso.append('fichier', compressedVerso)

        // Recto et verso en parallèle : le temps total n'est plus la somme des deux
        var responses = await Promise.all([
            axios.post(
                process.env.VUE_APP_API_URL + 'ocr/carte-grise',
                formDataRecto,
                { headers: Object.assign({}, buildMultipartHeaders(), { 'Content-Type': 'multipart/form-data' }) }
            ),
            axios.post(
                process.env.VUE_APP_API_URL + 'ocr/carte-grise',
                formDataVerso,
                { headers: Object.assign({}, buildMultipartHeaders(), { 'Content-Type': 'multipart/form-data' }) }
            )
        ])
        var responseRecto = responses[0]
        var responseVerso = responses[1]

        // Merge recto and verso data
        var combinedData = mergeOcrData(responseRecto.data, responseVerso.data)
        var normalizedData = normalizeOcrResponse(combinedData)

        if (!normalizedData || !normalizedData.immatriculation) {
            throw new Error('OCR_INCOMPLETE_DATA')
        }

        return normalizedData
    },

    /**
     * Validate file before upload
     * @param {File} file - File to validate
     * @returns {Object} Validation result
     */
    validateFile: function(file) {
        var allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
        var maxSize = 10 * 1024 * 1024 // 10MB

        if (!file) return { valid: false, error: 'NO_FILE' }
        if (!allowedTypes.includes(file.type)) return { valid: false, error: 'INVALID_TYPE' }
        if (file.size > maxSize) return { valid: false, error: 'FILE_TOO_LARGE' }
        return { valid: true }
    },

    /**
     * Get count of filled fields in extracted data
     * @param {Object} data - Extracted data
     * @returns {number} Number of filled fields
     */
    getFilledFieldsCount: function(data) {
        if (!data) return 0
        var count = 0
        var keys = Object.keys(data)
        for (var i = 0; i < keys.length; i++) {
            if (data[keys[i]] && data[keys[i]] !== '') {
                count++
            }
        }
        return count
    }
}
